import { useMemo, useState } from 'react';
import { useErrorHandlerContext, useOpeningElementsControllerContext, useUserLanguage } from '@/customHooks';
import {
  GetDepositsReportQuery,
  DepositsReportFilterInput,
  useGetDepositsReportQuery,
  useUpdateDepositMutation,
  DepositStatusOption,
} from '@/graphql/generated';
import { LoadingSpinner, PaymentStatusOptionPill } from '../atoms';
import { DepositStatusOptionPill } from '../atoms/DepositStatusOptionPill';
import textsData from '@/textData/reportTexts.json';

type DepositForList = NonNullable<GetDepositsReportQuery['depositsReport']>['items'][number];

const readableDate = (date: Date | string | null | undefined, userLanguage: 'es' | 'en') => {
  if (!date) return '-';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString(userLanguage === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
};

const formatAmount = (amount: string, currency: string, userLanguage: 'es' | 'en') => {
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return amount;
  return new Intl.NumberFormat(userLanguage === 'es' ? 'es-ES' : 'en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  }).format(numAmount);
};

type DepositsListTableProps = {
  filters: DepositsReportFilterInput;
};

export const DepositsListTable = ({ filters }: DepositsListTableProps) => {
  const userLanguage = useUserLanguage();
  const texts = (userLanguage === 'es' ? textsData.es : textsData.en).depositReport;

  const { open: errorHandle } = useErrorHandlerContext();
  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const { data, error, loading } = useGetDepositsReportQuery({
    variables: {
      filterInput: filters,
    },
  });

  const dataFlatter = (data: DepositForList[] | undefined) => {
    if (!data) return [];

    return data.map((element) => ({
      id: element.id,
      reference: element.reference || '-',
      ownerAccountName: element.ownerAccountName || '-',
      operationDate: element.operationDate,
      amount: formatAmount(element.amount, element.currencyOption, userLanguage),
      depositStatusOption: element.depositStatusOption,
      currency: element.currencyOption,
      payment: element.payment,
    }));
  };

  const flattenedData = useMemo(
    () => (data ? dataFlatter(data.depositsReport?.items) : null),
    [data, userLanguage]
  );

  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const [updateDeposit] = useUpdateDepositMutation({
    refetchQueries: ['GetDepositsReport'],
  });


  const handleUpdateStatus = async (id: string, status: DepositStatusOption) => {
    updateDeposit({
      variables: {
        id,
        input: { depositStatusOption: status },
      },
    })
    .then((response) => {
      confirmDialogModalOpen({
        title: 'Deposit status updated',
        description: `The deposit ID: ${response.data?.updateDeposit?.id ?? 'unknown'} status has been updated successfully`,
      })
    })
    .catch((error) => {
      errorHandle(error)
    })
    
  };

  if (error) errorHandle(error);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner text />
      </div>
    );
  }

  if (!flattenedData || flattenedData.length === 0) {
    return (
      <div className="w-full">
        <span className="font-bold">{texts.noData.table}</span>
      </div>
    );
  }

  return (
    <div className="pt-4 pb-4 rounded-xl grid grid-cols-[1.5fr_1.5fr_2fr_1fr_1fr] overflow-x-auto">
      {/* Header */}
      <div className="contents">
        <div className="font-bold text-left p-4 border-t border-b border-gray-400">
          {texts.depositsTable.reference}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400">
          {texts.depositsTable.ownerAccount}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400">
          {texts.depositsTable.operationDate}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400">
          {texts.depositsTable.amount}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400">
          {texts.depositsTable.status}
        </div>
      </div>

      {/* Rows */}
      {flattenedData.map((deposit, index) => {
        const rowBg = index % 2 !== 0 ? 'bg-background2' : 'bg-white';
        const isExpanded = expandedId === deposit.id;

        return (
          <div key={deposit.id} className="contents">
            <div
              onClick={() => toggleExpand(deposit.id)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch place-content-center cursor-pointer transition-colors ${rowBg}`}
            >
              {deposit.reference}
            </div>
            <div
              onClick={() => toggleExpand(deposit.id)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch place-content-center cursor-pointer transition-colors ${rowBg}`}
            >
              {deposit.ownerAccountName}
            </div>
            <div
              onClick={() => toggleExpand(deposit.id)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch place-content-center cursor-pointer transition-colors ${rowBg}`}
            >
              {readableDate(deposit.operationDate, userLanguage)}
            </div>
            <div
              onClick={() => toggleExpand(deposit.id)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch place-content-center cursor-pointer transition-colors ${rowBg}`}
            >
              {deposit.amount}
            </div>
            <div
              onClick={() => toggleExpand(deposit.id)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch place-content-center cursor-pointer transition-colors ${rowBg}`}
            >
              <DepositStatusOptionPill option={deposit.depositStatusOption} />
            </div>

            {/* Accordion Content */}
            {isExpanded && (
              <div className="col-start-1 col-end-6 bg-gray-50 pb-4 border-b border-gray-200">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex justify-between gap-4 items-center">
                  <div className="flex-1">
                    <h4 className="font-bold text-primary2 mb-4 border-b border-gray-100 pb-2">
                      {texts.depositDetail.paymentTitle}
                    </h4>
                    {deposit.payment ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">
                            {texts.depositDetail.paymentId}
                          </span>
                          <span className="text-sm font-medium text-gray-700 truncate" title={deposit.payment.id}>
                            {deposit.payment.id}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">
                            {texts.depositDetail.status}
                          </span>
                          <span className="text-sm font-medium text-gray-700">
                            <PaymentStatusOptionPill option={deposit.payment.paymentStatusOption} />
                          </span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">
                            {texts.depositDetail.amount}
                          </span>
                          <span className="text-sm font-bold text-primary1">
                            {formatAmount(deposit.payment.amountValue, deposit.currency, userLanguage)}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">
                            {texts.depositDetail.createdAt}
                          </span>
                          <span className="text-sm font-medium text-gray-700">
                            {readableDate(deposit.payment.createdAt, userLanguage)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex py-2">
                        <p className="text-sm text-gray-400 italic">
                          {texts.depositDetail.noPayment}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 min-w-[120px]">
                    <button
                      disabled={deposit.depositStatusOption === DepositStatusOption.Received}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateStatus(deposit.id, DepositStatusOption.Received);
                      }}
                      className="bg-primary1 text-white text-xs font-bold py-2 px-4 rounded hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {texts.depositDetail.reconcile}
                    </button>
                    <button
                      disabled={deposit.depositStatusOption === DepositStatusOption.Returned}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateStatus(deposit.id, DepositStatusOption.Returned);
                      }}
                      className="bg-orange-500 text-white text-xs font-bold py-2 px-4 rounded hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {texts.depositDetail.reject}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
